import { ReactElement, memo } from 'react';
import * as S from './Confirmation.styles';
import VoucherCard from '@/common/ui/cards/VoucherCard/VoucherCard';
import { useThemeContext } from '@/common/app/contexts/ThemeContext';
import { Spinner } from '@/common/ui/loaders/Spinner';
import { useCheckoutContext } from '@/common/app/contexts/CheckoutContext';
import Link from 'next/link';
import { IOrderDetail } from '@/entities/Checkout/domain/Checkout.domain';
import { TotalAmount } from '../TotalAmount/TotalAmount';

const Confirmation = ({
    isCheckout,
    assessOrder,
    grandTotal,
}: {
    isCheckout: boolean;
    assessOrder?: IOrderDetail;
    grandTotal?: number;
}): ReactElement => {
    const { theme } = useThemeContext();
    const {
        order: { detail },
        isLoading,
    } = useCheckoutContext();
    const currentOrder = assessOrder?.id ? assessOrder : detail;
    const isLight = theme.name === 'light';
    const orderID = currentOrder.id;
    const assessKey = currentOrder.accesskey;
    const cartItems = Object.values(currentOrder.cart_items);

    return (
        <S.Container data-test-id="Thankyou_page">
            {isCheckout && (
                <>
                    <S.PriceWrapper>
                        {!!grandTotal && <TotalAmount total={grandTotal} />}
                        <S.PriceTitle>Your adventure awaits</S.PriceTitle>
                    </S.PriceWrapper>
                    <S.ThankyouImage isLight={isLight} />
                </>
            )}

            {!isLoading && !orderID && (
                <div>
                    <>
                        <S.IfError>You have encountered an error.</S.IfError>
                        <S.ErrorText>
                            Our developes have been notified and are busily working to solve this
                            issue. However if you need immediate assistance feel free to use our
                            live chat or contact us via phone: <b>850-424-5125</b>
                            {currentOrder.accesskey && (
                                <>
                                    <hr />
                                    <a
                                        href={`/order/access?key=${currentOrder.accesskey}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Please use this url to resolve the problem.
                                    </a>
                                </>
                            )}
                        </S.ErrorText>
                    </>
                </div>
            )}
            {!isLoading && orderID && (
                <S.ImportantInfoWrapper>
                    <S.ImportantInfoTitle>Important information</S.ImportantInfoTitle>
                    <S.InfoWrapper>
                        <S.InfoSteps data-test-id="order-header">1. Get your voucher</S.InfoSteps>
                        <S.InfoStepsDetails>
                            We have emailed your vouchers to: {currentOrder.customer_email} and they
                            are listed below for your convenience.
                        </S.InfoStepsDetails>
                        {assessKey ? (
                            <>
                                {cartItems
                                    .filter((i) => i.state !== 'cancelled' && i.state !== 'deleted')
                                    .map((item) => (
                                        <VoucherCard
                                            item={item}
                                            accessKey={assessKey}
                                            key={item.id}
                                        />
                                    ))}
                                {cartItems
                                    .filter((i) => i.state === 'cancelled' || i.state === 'deleted')
                                    .map((item) => (
                                        <VoucherCard
                                            item={item}
                                            accessKey={assessKey}
                                            key={item.id}
                                            inactive
                                        />
                                    ))}
                            </>
                        ) : (
                            <S.SkeletonCard />
                        )}
                        <S.InfoSteps data-test-id="order-header">2. Get ready</S.InfoSteps>
                        <S.InfoStepsDetails>
                            Each activity partner has different suggestions on what to bring, what
                            to wear and how early to arrive. They are printed on the vouchers and
                            will ensure you are ready to have a great experience.
                        </S.InfoStepsDetails>
                        <S.InfoSteps data-test-id="order-header">3. Get excited!</S.InfoSteps>
                        <S.InfoStepsDetails>
                            Don&#39;t forget to tag us in your photos so we can see all the fun
                            you&#39;re having using <span>#tripshock</span> <br /> Follow us on
                            Facebook and Instagram to see inspiration for your vacation - and you
                            may just see your snapshots featured!
                        </S.InfoStepsDetails>
                    </S.InfoWrapper>
                </S.ImportantInfoWrapper>
            )}
            {isLoading ? (
                <Spinner isLoading />
            ) : (
                <S.SocialIconsWrapper data-test-id="Social_icons">
                    <Link href="https://facebook.com/tripshockgulfcoast" passHref>
                        <S.HugeSocialIcons data-test-id="Facebook">
                            <S.FacebookCircle>
                                <S.FacebookIcon />
                            </S.FacebookCircle>
                            Find us on Facebook
                        </S.HugeSocialIcons>
                    </Link>
                    <Link href="https://www.instagram.com/tripshock/" passHref>
                        <S.HugeSocialIcons data-test-id="Instagram">
                            <S.InstagramCircle>
                                <S.InstagramIcon />
                            </S.InstagramCircle>
                            Follow us on Instagram
                        </S.HugeSocialIcons>
                    </Link>
                </S.SocialIconsWrapper>
            )}
        </S.Container>
    );
};

export { Confirmation };
export default memo(Confirmation);
